import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { useIntercom } from "react-use-intercom";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";

import ErrorPage from "../../Components/Others/error-page";
import {
  buildCheckRequestAddress,
  dispatchErrorToast,
  dispatchSuccessToast,
  getBattlecardCashBackAmountNumeric,
  formatCurrency,
  getCheckIdFromDealsResponse,
  getLegacyCheckErrorFromDealsResponse,
  getLegacyCheckStatusValueFromDealsResponse,
  getUserId,
  isSVDEligible,
  isLegacyCheckNotProcessedFromDealsResponse,
  hasUnclaimedTransactions,
  getUserEmail,
  isInstantReleaseUserFromDealsResponse,
  onSignOut,
} from "../../Utils/helpers";

import {
  useDeals,
  useGetPartnerCustomerInformation,
  useIsCustomerFromPartnerOnboarded,
  useStakeDebit,
  useUnclaimedTransactions,
  useUserAccount,
} from "../../CustomHooks/Queries";
import { useRequestCheck, useUpdateAddress } from "../../CustomHooks/Mutations";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";
import { useModalContext } from "../../Contexts/ModalContext";
import renderInstantReleaseRequestCard from "../../Organisms/Modals/Instant Release/request-card-modal";
import { StakeCheckingWizard } from "../../Organisms/Modals/Stake Checking Status/stake-checking-onboarding-step";
import { APIQueryKeys } from "../../Utils/constants";
import {
  addressFromUser,
  buildUpdateAddressPayload,
  formatCheckStatusAddress,
  formatMailingDisplay,
  isAddressComplete,
  trimAddressFields,
} from "../Onboarding/check-address-utils";
import CheckMailingAddressEditForm from "../Onboarding/check-mailing-address-edit-form";
import { ADDRESS_VERIFY_ERROR_BANNER } from "../Onboarding/validate-address-modal";
import "../Onboarding/battle-cards.scss";
import {
  buildInitialForm,
  clearInstantReleasePending,
  readInstantReleasePending,
  readRewardAmount,
  screenForApplicationOutcome,
  screenFromBattlecardState,
  screenFromStatus,
} from "./instant-data";
import Approved from "./approved";
import CashBackDisclaimerScreen from "./cash-back-disclaimer";
import CheckTracker, { CHECK_STEP_BY_STATUS, CHECK_STEP_MAILED, CHECK_STEP_WAITING } from "./check-tracker";
import ConfirmAddress from "./confirm-address";
import Landing from "./landing";
import SettingUpOverlay from "./setting-up-overlay";
import Shell from "./shell";
import StatusPanel from "./status-panel";
import "./instant-release.scss";

const PENDING_POLL_INTERVAL_MS = 3 * 1000;
const APPLY_SETTLE_POLL_COUNT = 6;
const PENDING_POLL_TIMEOUT_MS = 10 * 60 * 1000;

function InstantRelease({
  isLoading,
  isError,
  errorMessage,
  isPageNotFound,
  partnerCustomer,
  onboardedCustomer,
  rewardAmount,
  status,
  onFinish,
  onSignOutClick,
}) {
  const navigate = useNavigate();
  const modalContext = useModalContext();
  const { show: showIntercom } = useIntercom();
  const amount = useMemo(() => {
    if (rewardAmount != null && Number(rewardAmount) > 0) return Number(rewardAmount);
    return readRewardAmount(partnerCustomer);
  }, [partnerCustomer, onboardedCustomer, rewardAmount]);
  const initialScreen = useMemo(() => {
    return screenFromStatus(status) || "landing";
  }, [status]);

  const [screen, setScreen] = useState(initialScreen);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const queryClient = useQueryClient();
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const prefilledRef = useRef(false);
  const [form, setForm] = useState(() => buildInitialForm(partnerCustomer, onboardedCustomer));
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState(null);
  const [savedAddress, setSavedAddress] = useState(null);
  const [showAddressError, setShowAddressError] = useState(false);
  const { data: userAccountData } = useUserAccount();
  const accountAddress = useMemo(() => addressFromUser(userAccountData?.data?.data?.user), [userAccountData]);
  const activeAddress = savedAddress ?? accountAddress;
  const addressDisplay = useMemo(() => {
    const display = formatMailingDisplay(activeAddress);
    return { name: display.name, lines: [display.street, display.cityLine].filter(Boolean) };
  }, [activeAddress]);

  useEffect(() => {
    if (prefilledRef.current || (!partnerCustomer && !onboardedCustomer)) return;
    setForm(buildInitialForm(partnerCustomer, onboardedCustomer));
    prefilledRef.current = true;
  }, [partnerCustomer, onboardedCustomer]);

  const { data: liveDeals } = useDeals();
  const checkStatus = liveDeals?.data?.data?.currentDeal?.check_status;
  const checkStatusValue = getLegacyCheckStatusValueFromDealsResponse(liveDeals);
  const trackerStep = CHECK_STEP_BY_STATUS[checkStatusValue] ?? (screen === "mailing" ? CHECK_STEP_MAILED : CHECK_STEP_WAITING);
  // The vendor can still change the address until the check is mailed.
  const canEditCheckAddress =
    !checkStatusValue || checkStatusValue === "property_approved" || checkStatusValue === "property_payment";
  const notProcessed = getLegacyCheckErrorFromDealsResponse(liveDeals);

  // After a check exists, show the address it was actually submitted with.
  const trackerAddress = useMemo(() => {
    const submitted = checkStatus?.address ? formatCheckStatusAddress(checkStatus.address) : null;
    if (submitted?.street) {
      return { name: submitted.name || addressDisplay.name, lines: [submitted.street, submitted.cityLine].filter(Boolean) };
    }
    return addressDisplay;
  }, [checkStatus, addressDisplay]);

  // Same request the battlecard legacy-check flow submits.
  const { mutate: requestCheck, isLoading: isSubmittingCheck } = useRequestCheck({
    onSuccess: async () => {
      await queryClient.refetchQueries(APIQueryKeys.deals);
      await queryClient.refetchQueries(APIQueryKeys.checkConfig);
      dispatchSuccessToast("Your check request has been submitted");
      if (isMountedRef.current) setScreen("waiting");
    },
    onError: async (err) => {
      await queryClient.refetchQueries(APIQueryKeys.deals);
      if (!isMountedRef.current) return;
      // A rejected request is surfaced by the not-processed screen, so a toast would only duplicate it.
      if (isLegacyCheckNotProcessedFromDealsResponse(queryClient.getQueryData(APIQueryKeys.deals))) {
        setScreen("notProcessed");
        return;
      }
      dispatchErrorToast(err?.response?.data?.error ?? "Unable to submit your check request. Please try again.");
    },
  });

  const { mutate: updateAddress, isLoading: isSavingAddress } = useUpdateAddress({
    onSuccess: async () => {
      await Promise.all([queryClient.refetchQueries(APIQueryKeys.deals), queryClient.refetchQueries(APIQueryKeys.userAccount)]);
      dispatchSuccessToast("Your mailing address has been updated");
      if (isMountedRef.current) setEditingAddress(false);
    },
    onError: () => setShowAddressError(true),
  });

  const submitCheckRequest = () => {
    const trimmed = trimAddressFields(activeAddress);
    if (!isAddressComplete(trimmed)) {
      dispatchErrorToast("Please fill in all required address fields.");
      return;
    }
    if (!amount) {
      dispatchErrorToast("We couldn’t find your Move-In Cash amount. Please contact support.");
      return;
    }
    requestCheck({
      mobileUserId: getUserId(),
      amount,
      address: buildCheckRequestAddress(trimmed),
    });
  };

  const finish = useCallback(() => {
    if (onFinish) onFinish();
    else navigate("/home");
  }, [navigate, onFinish]);

  useEffect(() => {
    if (checkStatusValue === "delivered") finish();
  }, [liveDeals, checkStatusValue, finish]);

  // Go home first, then offer the card request on top of the home screen.
  const continueFromApproved = useCallback(() => {
    finish();
    renderInstantReleaseRequestCard(modalContext);
  }, [finish, modalContext]);

  const returnToLanding = useCallback(() => setScreen("landing"), []);
  const showDenied = useCallback(() => {
    setIsSettingUp(false);
    setScreen("denied");
  }, []);
  const showApproved = useCallback(() => {
    setIsSettingUp(false);
    setScreen("approved");
  }, []);
  const showApplicationOutcome = useCallback(
    async (stakeDebit) => {
      let nextScreen = screenForApplicationOutcome(stakeDebit);
      // Right after applying, the status is often still pending for a few seconds. Keep the
      // "Setting up your account" overlay up and poll a handful of times before showing pending.
      if (nextScreen === "pending") {
        setIsSettingUp(true);
        for (let attempt = 0; attempt < APPLY_SETTLE_POLL_COUNT && nextScreen === "pending"; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, PENDING_POLL_INTERVAL_MS));
          if (!isMountedRef.current) return;
          await queryClient.refetchQueries(APIQueryKeys.stakeDebit);
          nextScreen = screenForApplicationOutcome(queryClient.getQueryData(APIQueryKeys.stakeDebit)?.data);
        }
      }
      if (!isMountedRef.current) return;
      setIsSettingUp(false);
      setScreen(nextScreen);
    },
    [queryClient]
  );

  // While the application is pending or in review, poll the debit status. Move to the approved
  // (or denied) screen as soon as it resolves (approved means the deposit account exists), and send the
  // member home if it is still unresolved
  // after PENDING_POLL_TIMEOUT_MS.
  const isWaitingOnStatus = screen === "pending" || screen === "review" || screen === "details";
  const isAwaitingApproval = screen === "pending" || screen === "review";
  const { data: polledStakeDebit } = useStakeDebit({
    enabled: isWaitingOnStatus,
    refetchInterval: isWaitingOnStatus ? PENDING_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (!isWaitingOnStatus || !polledStakeDebit?.data) return;
    const stakeDebit = polledStakeDebit.data;
    if (isSVDEligible(stakeDebit)) return;
    const nextScreen = screenForApplicationOutcome(stakeDebit);
    if (nextScreen !== screen) setScreen(nextScreen);
  }, [isWaitingOnStatus, polledStakeDebit, screen]);

  useEffect(() => {
    if (!isAwaitingApproval) return undefined;
    const timeout = setTimeout(finish, PENDING_POLL_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isAwaitingApproval, finish]);

  const signOut = () => {
    if (onSignOutClick) onSignOutClick();
    else onSignOut();
  };

  const openAddressEditor = () => {
    setAddressDraft({ ...activeAddress });
    setShowAddressError(false);
    setEditingAddress(true);
  };

  const updateAddressDraft = (field, value) => {
    setShowAddressError(false);
    setAddressDraft((current) => ({ ...current, [field]: value }));
  };

  const saveAddress = () => {
    const trimmed = trimAddressFields(addressDraft);
    if (!isAddressComplete(trimmed)) {
      dispatchErrorToast("Please fill in all required address fields.");
      return;
    }
    setShowAddressError(false);
    updateAddress(buildUpdateAddressPayload(trimmed, { withdrawalId: getCheckIdFromDealsResponse(liveDeals) }), {
      onSuccess: () => setSavedAddress(trimmed),
    });
  };

  if (isLoading) {
    return (
      <div className="instant-release instant-release-loading">
        <CircularProgress sx={{ color: "#54FF99" }} />
      </div>
    );
  }

  if (isError) {
    return <ErrorPage isPageNotFound={isPageNotFound} errorMessage={errorMessage} />;
  }

  const showWizard = screen === "wizard";

  let body = null;
  if (screen === "landing") {
    body = <Landing amount={amount} onNext={() => setScreen("wizard")} onChat={showIntercom} />;
  } else if (showWizard) {
    body = (
      <div className="battle-cards-page bc-member-setup-page ir-member-setup">
        <StakeCheckingWizard
          variant="page"
          hideMilestone
          onExit={returnToLanding}
          onComplete={finish}
          onVerificationRejected={showDenied}
          onDepositAccountCreated={showApproved}
          onApplied={showApplicationOutcome}
          onApplyingChange={setIsSettingUp}
        />
      </div>
    );
  } else if (screen === "approved") {
    body = <Approved amount={amount} onContinue={continueFromApproved} />;
  } else if (screen === "pending") {
    body = (
      <StatusPanel
        title="Your application is pending"
        body="We’ve received your application and it’s currently pending. We’ll email and text you as soon as there’s an update."
      />
    );
  } else if (screen === "review") {
    body = (
      <StatusPanel
        title="Your application is under review"
        body="We’re reviewing your application now. This usually takes a short time. We’ll email and text you as soon as there’s an update."
      />
    );
  } else if (screen === "details") {
    body = (
      <StatusPanel
        title="We need a few more details"
        body="Confirming your identity took an extra step this time. This will wrap it up."
        actionLabel="Verify"
        onAction={() => setScreen("wizard")}
      />
    );
  } else if (screen === "denied") {
    body = (
      <StatusPanel
        title="We couldn’t approve your application"
        body="We’re sorry, but we can’t approve your Stake Checking application at this time. You can still get your Move-In Cash by check. If you have questions, contact support."
        actionLabel="Request a check"
        onAction={() => setScreen("disclaimer")}
        secondaryLabel="Contact Support"
        onSecondary={showIntercom}
      />
    );
  } else if (screen === "disclaimer") {
    body = (
      <CashBackDisclaimerScreen
        onBack={() => setScreen("denied")}
        onContinue={() => setScreen("confirm")}
        onContact={showIntercom}
      />
    );
  } else if (screen === "confirm" || screen === "legacy") {
    body = (
      <ConfirmAddress
        address={addressDisplay}
        amount={amount}
        onBack={() => setScreen("disclaimer")}
        onEdit={openAddressEditor}
        onConfirm={submitCheckRequest}
        isSubmitting={isSubmittingCheck}
      />
    );
  } else if (screen === "notProcessed") {
    body = (
      <StatusPanel
        title={notProcessed.title}
        body={notProcessed.description}
        footnote={notProcessed.amount != null ? `Cash Back ${formatCurrency(notProcessed.amount)}` : null}
        showCards={false}
        actionLabel="Contact Support"
        onAction={showIntercom}
      />
    );
  } else if (screen === "waiting" || screen === "mailing") {
    body = (
      <CheckTracker
        address={trackerAddress}
        step={trackerStep}
        onEdit={canEditCheckAddress ? openAddressEditor : null}
      />
    );
  }

  return (
    <Shell
      email={form.email || getUserEmail()}
      onSupport={showIntercom}
      onSignOutClick={signOut}
      celebrate={screen === "approved"}
      centered={["landing", "approved", "pending", "review", "details", "denied", "notProcessed", "disclaimer", "legacy", "confirm", "waiting", "mailing"].includes(screen)}
      statusPage={["pending", "review", "details", "denied", "notProcessed", "disclaimer", "legacy", "confirm", "waiting", "mailing"].includes(screen)}
    >
      {body}
      {isSettingUp ? <SettingUpOverlay /> : null}
      {editingAddress && addressDraft ? (
        <div className="ir-modal-layer" role="dialog" aria-modal="true" aria-label="Update mailing address">
          <div className="ir-legacy-form-dialog">
            <CheckMailingAddressEditForm
              draft={addressDraft}
              onFieldChange={updateAddressDraft}
              onSave={saveAddress}
              onCancel={() => setEditingAddress(false)}
              isSaving={isSavingAddress}
              errorBanner={showAddressError ? ADDRESS_VERIFY_ERROR_BANNER : null}
            />
          </div>
        </div>
      ) : null}
    </Shell>
  );
}

export default function InstantReleasePage() {
  const navigate = useNavigate();
  const { setAppUtilityProps } = useAppUtilityContext();
  const pending = useMemo(() => readInstantReleasePending(), []);
  const partnerId = pending?.partnerId || "";
  const customerReferenceId = pending?.customerReferenceId || "";
  const hasRequiredParams = Boolean(partnerId && customerReferenceId);

  useEffect(() => {
    setAppUtilityProps((props) => ({ ...props, hideNavigationBar: true }));
    return () => setAppUtilityProps((props) => ({ ...props, hideNavigationBar: false }));
  }, [setAppUtilityProps]);

  const { isLoading: isLoadingDeals, data: dealsData } = useDeals();
  const { isLoading: isLoadingStakeDebit, data: stakeDebitData } = useStakeDebit();
  const { isLoading: isLoadingUnclaimed, data: unclaimedTransactionsData } = useUnclaimedTransactions();
  const accountLoading = isLoadingDeals || isLoadingStakeDebit || isLoadingUnclaimed;
  const rewardAmount = getBattlecardCashBackAmountNumeric(dealsData, unclaimedTransactionsData);
  // Gate entry on the unclaimed rebate row, same as battlecard. A `status` from the URL can pick the
  // screen, but only when the member still has an unclaimed row; otherwise fall back to the state check.
  const statusScreen = hasUnclaimedTransactions(unclaimedTransactionsData) ? screenFromStatus(pending?.status) : null;
  const liveScreen =
    statusScreen || screenFromBattlecardState(dealsData, unclaimedTransactionsData, stakeDebitData);
  const liveIsInstantReleaseUser = isInstantReleaseUserFromDealsResponse(dealsData);

  // Decide the entry screen once. Submitting the application changes the debit status and
  // refetches deals/unclaimed data, which would otherwise make the entry checks fail and
  // send the user home before the application outcome (pending, review, denied...) is shown.
  const entryRef = useRef(null);
  if (!entryRef.current && !accountLoading && liveIsInstantReleaseUser && liveScreen) {
    entryRef.current = { screen: liveScreen };
  }
  const hasEntered = Boolean(entryRef.current);
  const matchedScreen = entryRef.current?.screen ?? liveScreen;
  const isInstantReleaseUser = hasEntered || liveIsInstantReleaseUser;

  useEffect(() => {
    if (accountLoading || hasEntered || liveIsInstantReleaseUser) return;
    navigate("/home", { replace: true });
  }, [accountLoading, hasEntered, liveIsInstantReleaseUser, navigate]);

  useEffect(() => {
    if (accountLoading || hasEntered || !liveIsInstantReleaseUser || liveScreen) return;
    navigate("/home", { replace: true });
  }, [accountLoading, hasEntered, liveIsInstantReleaseUser, liveScreen, navigate]);

  const {
    isFetching: isCheckingCustomer,
    data: customerFromPartnerOnboarded,
    error: customerFromPartnerOnboardedError,
    isError: isCustomerError,
  } = useIsCustomerFromPartnerOnboarded(
    { partner_id: partnerId, partner_customer_id: customerReferenceId },
    { enabled: hasRequiredParams }
  );

  const {
    isLoading: isLoadingCustomer,
    data: partnerCustomerInformation,
    error: partnerCustomerInformationError,
    isError: isPartnerError,
  } = useGetPartnerCustomerInformation(
    { partner_id: partnerId, partner_customer_id: customerReferenceId },
    { enabled: hasRequiredParams }
  );

  if (accountLoading || !isInstantReleaseUser || !matchedScreen) {
    return (
      <div className="instant-release instant-release-loading">
        <CircularProgress sx={{ color: "#54FF99" }} />
      </div>
    );
  }

  const errorMessage =
    (isCustomerError
      ? customerFromPartnerOnboardedError?.response?.data?.error
      : isPartnerError
      ? partnerCustomerInformationError?.response?.data?.error
      : "") || "Network Error";

  const leave = () => {
    clearInstantReleasePending();
    navigate("/home", { replace: true });
  };

  const signOut = () => {
    clearInstantReleasePending();
    onSignOut();
  };

  return (
    <InstantRelease
      isLoading={hasRequiredParams && (isCheckingCustomer || isLoadingCustomer)}
      isError={isCustomerError || isPartnerError}
      errorMessage={errorMessage}
      isPageNotFound={customerFromPartnerOnboardedError?.response?.status === 404}
      partnerCustomer={partnerCustomerInformation?.data?.data}
      onboardedCustomer={customerFromPartnerOnboarded?.data?.data}
      rewardAmount={rewardAmount}
      status={matchedScreen}
      onFinish={leave}
      onSignOutClick={signOut}
    />
  );
}
